import React, { useCallback, useEffect, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { stayExtensionText } from '../i18n/pages/stayExtension';
import {
  StayExtensionDeclineReason,
  StayExtensionEnd,
  StayExtensionReservationSummary,
  StayExtensionType,
  getStayExtensionSummary,
  reopenStayExtensions,
} from '../api.stayExtension';
import './StayExtensionReservationBlock.css';

interface Props {
  reservationId: number;
}

/**
 * What this guest bought, and whether either end is shut off.
 *
 * <p>The reopen button is what makes "also close other times" safe to tick on the decline sheet:
 * without it, one tap closes an end permanently and the pending queue — which lists only pending
 * requests — has nowhere to show it, let alone reverse it.
 *
 * <p>It earns its place beyond that though. A manager looking at a reservation has no way today to
 * see whether extra hours were bought or what was paid; they would have to open the guest's own
 * check-in guide.
 */
const StayExtensionReservationBlock: React.FC<Props> = ({ reservationId }) => {
  const { lang } = useLang();
  const t = stayExtensionText[lang];

  const [summary, setSummary] = useState<StayExtensionReservationSummary | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setSummary(await getStayExtensionSummary(reservationId));
    } catch {
      setSummary(null);
    }
  }, [reservationId]);

  useEffect(() => { void load(); }, [load]);

  const reopen = async (type: StayExtensionType) => {
    setBusy(true);
    try {
      setSummary(await reopenStayExtensions(reservationId, type));
    } catch {
      await load();
    } finally {
      setBusy(false);
    }
  };

  // Nothing to say about an apartment that does not sell this at all.
  if (!summary?.enabled) return null;

  const fill = (s: string, v: Record<string, string>) =>
    Object.entries(v).reduce((a, [k, x]) => a.replace(`{${k}}`, x), s);
  const money = (v: number | null) =>
    v === null ? '' : `${v.toLocaleString(lang === 'en' ? 'en-GB' : lang)} CZK`;
  const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '');
  const at = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleString(undefined, {
          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
        })
      : '';

  const reasons: Record<StayExtensionDeclineReason, string> = {
    CLEANING: t.mCleaning,
    OTHER_BOOKING: t.mOtherBooking,
    SHORT_NOTICE: t.mShortNotice,
    NO_SPACE: t.mNoSpace,
    UNSPECIFIED: t.mUnspecified,
  };

  const row = (end: StayExtensionEnd, label: string) => (
    <div className="sxr-row" key={end.type}>
      <span className="k">{label}</span>
      <span className="v">{end.time}</span>
      <span className={`n${end.granted ? ' paid' : ''}`}>
        {end.granted && end.pricePaid !== null
          ? fill(t.rdPaid, { price: money(end.pricePaid), date: day(end.paidAt) })
          : t.rdStandard}
      </span>
    </div>
  );

  /**
   * Two kinds of waiting, worded differently on purpose: AWAITING_MANAGER is the host's move,
   * AWAITING_PAYMENT is the guest's. Rendering them identically would send a host chasing the
   * wrong person — so the one that needs the host gets the stronger styling.
   */
  const waitingNotice = (end: StayExtensionEnd) => {
    if (!end.waitingOn) return null;
    const mine = end.waitingOn === 'AWAITING_MANAGER';
    return (
      <div className={`sxr-waiting${mine ? ' mine' : ''}`} key={`${end.type}-waiting`}>
        <span className="t">⏳</span>
        <span className="d">
          {mine
            ? fill(t.rdWaitingManager, {
                time: end.waitingTime ?? '',
                since: at(end.waitingSince),
              })
            : fill(t.rdWaitingPayment, {
                time: end.waitingTime ?? '',
                price: money(end.waitingPrice),
                until: at(end.waitingUntil),
              })}
        </span>
      </div>
    );
  };

  const closedNotice = (end: StayExtensionEnd) => {
    if (!end.closed) return null;
    const title = end.type === 'EARLY_CHECKIN' ? t.rdClosedEarly
      : end.type === 'LATE_CHECKOUT' ? t.rdClosedLate
      : end.type === 'LUGGAGE_DROPOFF' ? t.rdClosedLuggageIn
      : t.rdClosedLuggageOut;
    return (
      <div className="sxr-closed" key={`${end.type}-closed`}>
        <span className="t">⚠ {title}</span>
        <span className="d">
          {fill(t.rdClosedDetail, {
            date: day(end.closedAt),
            reason: end.closedReason ? `${reasons[end.closedReason]}.` : '',
          })}
        </span>
        <button type="button" className="b" disabled={busy} onClick={() => reopen(end.type)}>
          {t.rdReopen}
        </button>
      </div>
    );
  };

  return (
    <div className="sxr">
      <h4>{t.rdTitle}</h4>
      {row(summary.checkin, t.rdCheckin)}
      {waitingNotice(summary.checkin)}
      {row(summary.checkout, t.rdCheckout)}
      {waitingNotice(summary.checkout)}
      {/* Null whenever the apartment does not sell storage, so a host who only sells hours sees
          exactly the block they saw before. */}
      {summary.luggageDropoff && row(summary.luggageDropoff, t.rdLuggageDropoff)}
      {summary.luggageDropoff && waitingNotice(summary.luggageDropoff)}
      {summary.luggagePickup && row(summary.luggagePickup, t.rdLuggagePickup)}
      {summary.luggagePickup && waitingNotice(summary.luggagePickup)}
      {closedNotice(summary.checkin)}
      {closedNotice(summary.checkout)}
      {summary.luggageDropoff && closedNotice(summary.luggageDropoff)}
      {summary.luggagePickup && closedNotice(summary.luggagePickup)}
    </div>
  );
};

export default StayExtensionReservationBlock;
